import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

const builtInDatumsGroupId = "builtInDatumsGroup";

interface WallDatumProps {
  readonly entityType: string;
  readonly id: string;
  readonly sortIndex: number;
  readonly name: string;
  readonly datumType: string;
  readonly direction: string;
  readonly offsetType: string;
  readonly anchor: string;
  readonly justification: string;
  readonly offset: number;
  readonly offsetNormalized: boolean;
}

export enum SpanExtrema {
  BASE_WALL_BOTTOM = "base_wall_bottom",
  BASE_WALL_TOP = "base_wall_top",
  GABLE_END_BOTTOM = "gable_end_bottom",
  GABLE_END_TOP = "gable_end_top",
  WALL_START = "wall_start",
  WALL_END = "wall_end",
}

// built-in datums
export const builtInDatums: { readonly [id: string]: WallDatumProps } = {
  [SpanExtrema.BASE_WALL_BOTTOM]: {
    entityType: "DATUM",
    id: SpanExtrema.BASE_WALL_BOTTOM,
    sortIndex: 0,
    name: "Base wall bottom",
    datumType: "WALL",
    direction: "VERTICAL",
    offsetType: "RELATIVE",
    offset: 0,
    anchor: "BASE_WALL",
    justification: "BOTTOM_UP",
    offsetNormalized: false,
  },
  [SpanExtrema.BASE_WALL_TOP]: {
    entityType: "DATUM",
    id: SpanExtrema.BASE_WALL_TOP,
    sortIndex: 1,
    name: "Base wall top",
    datumType: "WALL",
    direction: "VERTICAL",
    offsetType: "RELATIVE",
    offset: 0,
    anchor: "BASE_WALL",
    justification: "TOP_DOWN",
    offsetNormalized: false,
  },
  [SpanExtrema.GABLE_END_BOTTOM]: {
    entityType: "DATUM",
    id: SpanExtrema.GABLE_END_BOTTOM,
    sortIndex: 2,
    name: "Gable end bottom",
    datumType: "WALL",
    direction: "VERTICAL",
    offsetType: "RELATIVE",
    offset: 0,
    anchor: "GABLE_END",
    justification: "BOTTOM_UP",
    offsetNormalized: false,
  },
  [SpanExtrema.GABLE_END_TOP]: {
    entityType: "DATUM",
    id: SpanExtrema.GABLE_END_TOP,
    sortIndex: 3,
    name: "Gable end top",
    datumType: "WALL",
    direction: "VERTICAL",
    offsetType: "RELATIVE",
    offset: 0,
    anchor: "GABLE_END",
    justification: "TOP_DOWN",
    offsetNormalized: false,
  },
  [SpanExtrema.WALL_START]: {
    entityType: "DATUM",
    id: SpanExtrema.WALL_START,
    sortIndex: 4,
    name: "Wall start",
    datumType: "WALL",
    direction: "HORIZONTAL",
    offsetType: "RELATIVE",
    offset: 0,
    anchor: "OUTSIDE_OF_SURFACE",
    justification: "START_TO_END",
    offsetNormalized: false,
  },
  [SpanExtrema.WALL_END]: {
    entityType: "DATUM",
    id: SpanExtrema.WALL_END,
    sortIndex: 5,
    name: "Wall end",
    datumType: "WALL",
    direction: "HORIZONTAL",
    offsetType: "RELATIVE",
    offset: 0,
    anchor: "OUTSIDE_OF_SURFACE",
    justification: "END_TO_START",
    offsetNormalized: false,
  },
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_473: DocMigration = {
  maxSchemaVersion: "0.0.473",
  description: "Migrate wall assembly domain datums",
  apply(json: any) {
    const {
      edgeBehaviors = {},
      datums = {},
      groups = {},
      entityGroups = {},
      rooms = {},
      facadeSpanStrata = {},
      facadeSweepStrata = {},
    } = json;

    const updatedEdgeBehaviors = {
      ...edgeBehaviors,
    };

    const updatedDatums = {
      ...datums,
    };

    const updatedGroups = {
      ...groups,
    };

    const updatedEntityGroups = {
      ...entityGroups,
    };

    const updatedFacadeSpanStrata = {
      ...facadeSpanStrata,
    };

    const updatedFacadeSweepStrata = {
      ...facadeSweepStrata,
    };

    // add missing built-in datums
    // (some very old documents may not have all the built-in datums)
    restoreBuiltInDatums(
      updatedDatums,
      updatedGroups,
      updatedEntityGroups,
      updatedFacadeSpanStrata,
      updatedFacadeSweepStrata,
    );

    let sortIndex = Object.keys(datums).length;
    let groupSortIndex = Object.keys(groups).length;

    const roomGroupIds: { [roomId: string]: string } = {};

    const addMigratedDatumToGroup = (datumId: string, parentId: string, parentName: string) => {
      // look for an existing group id for the parent
      let datumGroupId = roomGroupIds[parentId];

      // if the group id is not found, create a new group
      if (!datumGroupId) {
        datumGroupId = uid();
        roomGroupIds[parentId] = datumGroupId;

        updatedGroups[datumGroupId] = {
          entityType: "GROUP",
          name: `${parentName} Datums`,
          id: datumGroupId,
          sortIndex: groupSortIndex++,
        };
      }

      // add the datum to the group
      updatedEntityGroups[`datum.${datumId}`] = datumGroupId;
    };

    const migrateVerticalDatum = (
      parentId: string,
      parentName: string,
      edgeId: string,
      datum: any,
      prefix: string,
    ): { datumId: string; datumOffset?: number } => {
      // look for a built-in datum that matches the wall assembly override datum
      const builtInDatum = findBuiltInDatum(datum, builtInDatums);
      if (builtInDatum) {
        return builtInDatum;
      }

      // if no built-in datum is found, create a new datum
      const id = uid();

      updatedDatums[id] = {
        ...datum,
        id,
        name: `${parentName} Edge ${edgeId} ${prefix} Datum`,
        sortIndex: sortIndex++,
        entityType: "DATUM",
        datumType: "WALL",
        direction: "VERTICAL",
        anchor: datum.anchor ?? "BASE_WALL",
        justification: datum.justification ?? "BOTTOM_UP",
      };

      addMigratedDatumToGroup(id, parentId, parentName);

      return { datumId: id };
    };

    const migrateHorizontalDatum = (
      parentId: string,
      parentName: string,
      edgeId: string,
      datum: any,
      prefix: string,
    ): { datumId: string; datumOffset?: number } => {
      // look for a built-in datum that matches the wall assembly override datum
      const builtInDatum = findBuiltInDatum(datum, builtInDatums);
      if (builtInDatum) {
        return builtInDatum;
      }

      // if no built-in datum is found, create a new datum
      const id = uid();

      updatedDatums[id] = {
        ...datum,
        id,
        name: datum.name ?? `${parentName} Edge ${edgeId} ${prefix} Datum`,
        sortIndex: sortIndex++,
        entityType: "DATUM",
        datumType: "WALL",
        direction: "HORIZONTAL",
        anchor: datum.anchor ?? "OUTSIDE_OF_SURFACE",
        offsetNormalized: datum.offsetNormalized ?? true,
        justification: datum.justification ?? "START_TO_END",
      };

      addMigratedDatumToGroup(id, parentId, parentName);

      return { datumId: id };
    };

    for (const edgeBehaviorId in edgeBehaviors) {
      const edgeBehavior = edgeBehaviors[edgeBehaviorId];

      if (edgeBehavior.edgeBehaviorType !== "WALL_ASSEMBLY") {
        continue;
      }

      const { wallAssemblyOperator, parentId, edgeId } = edgeBehavior;
      const { operationType } = wallAssemblyOperator;

      if (operationType !== "ADD" && operationType !== "REMOVE" && operationType !== "OVERRIDE") {
        continue;
      }

      const { layer } = wallAssemblyOperator;

      if (!layer) {
        continue;
      }

      const parentName = rooms[parentId]?.name ?? "Unknown Room";
      const { lowerDatum, upperDatum, startDatum, endDatum } = layer;

      const updatedLayer = {
        ...layer,
      };

      if (lowerDatum) {
        const { datumId, datumOffset } = migrateVerticalDatum(parentId, parentName, edgeId, lowerDatum, "Lower");

        updatedLayer.lowerDatum = datumId;
        updatedLayer.lowerDatumOffset = datumOffset;
      }

      if (upperDatum) {
        const { datumId, datumOffset } = migrateVerticalDatum(parentId, parentName, edgeId, upperDatum, "Upper");

        updatedLayer.upperDatum = datumId;
        updatedLayer.upperDatumOffset = datumOffset;
      }

      if (startDatum) {
        const { datumId, datumOffset } = migrateHorizontalDatum(parentId, parentName, edgeId, startDatum, "Start");

        updatedLayer.startDatum = datumId;
        updatedLayer.startDatumOffset = datumOffset;
      }

      if (endDatum) {
        const { datumId, datumOffset } = migrateHorizontalDatum(parentId, parentName, edgeId, endDatum, "End");

        updatedLayer.endDatum = datumId;
        updatedLayer.endDatumOffset = datumOffset;
      }

      updatedEdgeBehaviors[edgeBehaviorId] = {
        ...edgeBehavior,
        wallAssemblyOperator: {
          ...wallAssemblyOperator,
          layer: updatedLayer,
        },
      };
    }

    return {
      ...json,
      edgeBehaviors: updatedEdgeBehaviors,
      datums: updatedDatums,
      groups: updatedGroups,
      entityGroups: updatedEntityGroups,
      facadeSpanStrata: updatedFacadeSpanStrata,
      facadeSweepStrata: updatedFacadeSweepStrata,
    };
  },
};

function findBuiltInDatum(
  datum: WallDatumProps,
  builtInDatums: { readonly [id: string]: WallDatumProps },
): { datumId: string; datumOffset?: number } | undefined {
  for (const datumId in builtInDatums) {
    const builtInDatum = builtInDatums[datumId];
    const fullDatumProps = { ...builtInDatum, ...datum };

    // check if the built-in datum is equal to the wall assembly override datum
    // we can ignore the normalized offset flag because built-in datums are having a 0 offset,
    // and a 0 offset resolves to the same value for normalized and non-normalized offsets
    if (areEqualButDifferentNormalization(fullDatumProps, builtInDatum)) {
      return { datumId };
    }

    // if the wall assembly override datum has a non-zero and non-normalized offset, we can use a built-in datum
    // and an extra offset
    if (areEqualButDifferentOffset(fullDatumProps, builtInDatum)) {
      // get the extra offset taking into account the justification for the offset sign
      const datumOffset =
        fullDatumProps.justification === "START_TO_END" || fullDatumProps.justification === "BOTTOM_UP"
          ? fullDatumProps.offset
          : -fullDatumProps.offset;

      return {
        datumId,
        datumOffset,
      };
    }
  }

  // no matching built-in datum found
  return undefined;
}

function areEqualButDifferentNormalization(a: WallDatumProps, b: WallDatumProps): boolean {
  return a.anchor === b.anchor && a.justification === b.justification && Math.abs(a.offset - b.offset) < 1e-6;
}

function areEqualButDifferentOffset(a: WallDatumProps, b: WallDatumProps): boolean {
  return a.anchor === b.anchor && a.justification === b.justification && a.offsetNormalized === b.offsetNormalized;
}

function restoreBuiltInDatums(datums: any, groups: any, entityGroups: any, spanStratums: any, sweepStratums: any) {
  let sortIndex = Object.keys(datums).length;

  // add built-in datums group if missing
  if (!groups[builtInDatumsGroupId]) {
    groups[builtInDatumsGroupId] = {
      entityType: "GROUP",
      name: "Built-in Datums",
      id: builtInDatumsGroupId,
      sortIndex: Object.keys(groups).length,
      isBuiltIn: true,
    };
  }

  // check for missing built-in datums
  for (const datumId in builtInDatums) {
    if (datums[datumId]) {
      continue;
    }

    // add the missing datum
    datums[datumId] = {
      ...builtInDatums[datumId],
      sortIndex: sortIndex++,
    };

    // add the missing datum to the built-in datums group
    entityGroups[`datum.${datumId}`] = builtInDatumsGroupId;

    // remove all usages of the missing datum so they can default to the proper defaults
    // (if we don't do this some very old documents may regress because they were expecting to not find the datum
    // and use the default datum, and after restoring the datum they can be using a wrong datum)
    removeDatumFromSpanStratas(spanStratums, datumId);
    removeDatumFromSweepStratas(sweepStratums, datumId);
  }
}

function removeDatumFromSpanStratas(spanStratums: any, datumId: string) {
  for (const spanStratumId in spanStratums) {
    const spanStratum = spanStratums[spanStratumId];

    if (spanStratum.lowerDatum === datumId) {
      delete spanStratum.lowerDatum;
    }

    if (spanStratum.upperDatum === datumId) {
      delete spanStratum.upperDatum;
    }

    if (spanStratum.startDatum === datumId) {
      delete spanStratum.startDatum;
    }

    if (spanStratum.endDatum === datumId) {
      delete spanStratum.endDatum;
    }
  }
}

function removeDatumFromSweepStratas(sweepStratums: any, datumId: string) {
  for (const sweepStratumId in sweepStratums) {
    const sweepStratum = sweepStratums[sweepStratumId];

    if (sweepStratum.datum === datumId) {
      delete sweepStratum.datum;
    }
  }
}

export default migration_0_0_473;
